import React, { useState, useEffect } from "react";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import {
  BadgeCheck, Building2, Plus, Image as ImageIcon,
  Search, ChevronRight, Package, MapPin, User, Mail, Lock,
  ChevronLeft, Clock, TrendingUp, ShoppingBag,
} from "lucide-react";
import { getImageUrl } from "../../utils/getImageUrl";

const STATUS_STYLES = {
  Activo:      "bg-green-50 text-green-700 border-green-200",
  Inactivo:    "bg-gray-100 text-gray-500 border-gray-200",
  En_revision: "bg-amber-50 text-amber-700 border-amber-200",
};

const PAGE_SIZE = 20;

export default function CompanyManager({ onSelectCompany }) {
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [form, setForm] = useState({
    name: "", slug: "", description: "", category: "", logo: null,
    ownerName: "", ownerEmail: "", ownerPassword: "",
  });
  const [previewLogo, setPreviewLogo] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchCompanies = async (p = page, s = search, st = filterStatus) => {
    try {
      const params = new URLSearchParams({ page: p, limit: PAGE_SIZE });
      if (s.trim()) params.set("search", s.trim());
      if (st !== "all") params.set("status", st);
      const res = await API.get(`/companies?${params}`);
      const d = res.data?.data ?? {};
      setCompanies(d.data ?? []);
      setTotal(d.total ?? 0);
      setTotalPages(d.pages ?? 1);
    } catch {
      toast.error("Error al cargar empresas");
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await API.get("/categories");
      setCategories(res.data?.data?.categories ?? res.data?.data ?? []);
    } catch {}
  };

  const fetchOverview = async () => {
    try {
      setOverviewLoading(true);
      const res = await API.get("/metrics/admin/overview");
      setOverview(res.data?.data ?? null);
    } catch {} finally {
      setOverviewLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); fetchOverview(); }, []);

  // Re-fetch when page, search or status changes
  useEffect(() => { fetchCompanies(page, search, filterStatus); }, [page]); // eslint-disable-line

  // Reset to page 1 when filters change (triggers the effect above via page change or direct call)
  const applySearch = (val) => { setSearch(val); setPage(1); fetchCompanies(1, val, filterStatus); };
  const applyStatus = (val) => { setFilterStatus(val); setPage(1); fetchCompanies(1, search, val); };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm({ ...form, logo: file });
    setPreviewLogo(URL.createObjectURL(file));
  };

  const createCompany = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug || !form.category) {
      toast.warn("Nombre, slug y categoría son obligatorios");
      return;
    }
    if (!form.ownerEmail || !form.ownerPassword) {
      toast.warn("El correo y contraseña del propietario son obligatorios");
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("slug", form.slug);
      fd.append("description", form.description);
      fd.append("category", form.category);
      fd.append("ownerName", form.ownerName);
      fd.append("ownerEmail", form.ownerEmail);
      fd.append("ownerPassword", form.ownerPassword);
      if (form.logo instanceof File) fd.append("logo", form.logo);
      await API.post("/companies", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Empresa creada correctamente");
      setForm({ name: "", slug: "", description: "", category: "", logo: null, ownerName: "", ownerEmail: "", ownerPassword: "" });
      setPreviewLogo(null);
      setShowForm(false);
      fetchCompanies(1, search, filterStatus);
      setPage(1);
    } catch (err) {
      toast.error(err.response?.data?.msg || err.response?.data?.error || "Error al crear la empresa");
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (e, companyId, currentStatus) => {
    e.stopPropagation();
    try {
      const newStatus = currentStatus === "Activo" ? "Inactivo" : "Activo";
      const res = await API.patch(`/companies/${companyId}/status`, { status: newStatus });
      const updated = res.data?.data?.company ?? res.data?.data ?? {};
      setCompanies((prev) =>
        prev.map((c) => c.id === companyId ? { ...c, status: updated.status ?? newStatus } : c)
      );
      toast.success(`Empresa ${newStatus === "Activo" ? "activada" : "desactivada"}`);
    } catch { toast.error("Error cambiando estado"); }
  };

  const toggleVerification = async (e, companyId, currentVerified) => {
    e.stopPropagation();
    try {
      const res = await API.patch(`/companies/${companyId}/status`, { verified: !currentVerified });
      const updated = res.data?.data?.company ?? res.data?.data ?? {};
      setCompanies((prev) =>
        prev.map((c) => c.id === companyId ? { ...c, verified: updated.verified ?? !currentVerified } : c)
      );
      toast.success(currentVerified ? "Verificación removida" : "Empresa verificada");
    } catch { toast.error("Error cambiando verificación"); }
  };

  return (
    <div className="w-full max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-gray-800">Empresas</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {total} registradas · página {page} de {totalPages}
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 px-4 py-2 bg-supernova-500 hover:bg-supernova-600 text-white rounded-xl text-sm font-semibold transition shrink-0"
        >
          <Plus size={16} />
          {showForm ? "Cancelar" : "Nueva empresa"}
        </button>
      </div>

      {/* Stats bar */}
      <div className="space-y-3">
        {/* Fila 1: filtros por estado */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Activas",     id: "Activo",      color: "text-green-600", bg: "bg-green-50  border-green-100", val: overview?.totals?.activo },
            { label: "Inactivas",   id: "Inactivo",    color: "text-gray-500",  bg: "bg-gray-50   border-gray-100",  val: overview?.totals?.inactivo },
            { label: "En revisión", id: "En_revision", color: "text-amber-600", bg: "bg-amber-50  border-amber-100", val: overview?.totals?.enRevision },
          ].map(({ label, id, color, bg, val }) => (
            <div key={id} onClick={() => applyStatus(filterStatus === id ? "all" : id)}
              className={`rounded-xl border px-4 py-3 cursor-pointer transition hover:opacity-80 ${bg} ${filterStatus === id ? "ring-2 ring-offset-1 ring-gray-400" : ""}`}>
              <p className={`text-xl font-black ${color}`}>
                {overviewLoading ? "—" : (val ?? "—")}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Fila 2: métricas de actividad en tiempo real */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Clock size={13} className="text-emerald-500" />
              <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide">Ahora abiertos</span>
            </div>
            <p className="text-2xl font-black text-emerald-700">
              {overviewLoading ? "—" : (overview?.openNow ?? 0)}
            </p>
            <p className="text-[10px] text-emerald-500 mt-0.5">con horario activo hoy</p>
          </div>

          <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
            <div className="flex items-center gap-1.5 mb-1">
              <ShoppingBag size={13} className="text-blue-500" />
              <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wide">Activos hoy</span>
            </div>
            <p className="text-2xl font-black text-blue-700">
              {overviewLoading ? "—" : (overview?.activeToday ?? 0)}
            </p>
            <p className="text-[10px] text-blue-500 mt-0.5">con pedidos en el día</p>
          </div>

          <div className="rounded-xl border border-violet-100 bg-violet-50 px-4 py-3">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp size={13} className="text-violet-500" />
              <span className="text-[10px] font-bold text-violet-600 uppercase tracking-wide">Últimos 7 días</span>
            </div>
            <p className="text-2xl font-black text-violet-700">
              {overviewLoading ? "—" : (overview?.activeLast7 ?? 0)}
            </p>
            <p className="text-[10px] text-violet-500 mt-0.5">comercios con pedidos</p>
          </div>
        </div>

        {/* Fila 3: comercios más activos hoy */}
        {!overviewLoading && overview?.topToday?.length > 0 && (
          <div className="rounded-xl border border-gray-100 bg-white px-4 py-3">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <ShoppingBag size={11} /> Más activos hoy
            </p>
            <div className="flex flex-wrap gap-2">
              {overview.topToday.map((c) => (
                <div key={c.id}
                  onClick={() => onSelectCompany?.(c)}
                  className="flex items-center gap-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-100 rounded-lg px-2.5 py-1.5 cursor-pointer transition">
                  {c.logo
                    ? <img src={getImageUrl(c.logo)} alt="" className="w-5 h-5 rounded object-cover" />
                    : <div className="w-5 h-5 rounded bg-gray-200 flex items-center justify-center text-[8px] font-bold text-gray-500">{c.name[0]}</div>
                  }
                  <span className="text-xs font-semibold text-gray-700">{c.name}</span>
                  <span className="text-[10px] font-black text-blue-600 bg-blue-50 rounded px-1">{c._count?.orders ?? 0}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create form (collapsible) */}
      {showForm && (
        <motion.form
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={createCompany}
          className="bg-white shadow-sm rounded-2xl p-5 border border-gray-100 space-y-4"
        >
          {/* Datos del comercio */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Building2 size={12} /> Datos del comercio
            </p>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              <input
                placeholder="Nombre de la empresa *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
              />
              <input
                placeholder="Slug (ej: mi-tienda) *"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
              />
              <input
                placeholder="Descripción"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
              />
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
              >
                <option value="">Seleccionar categoría *</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>

              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 bg-gray-50 cursor-pointer px-3 py-2.5 rounded-xl hover:bg-gray-100 border border-gray-200 transition text-sm text-gray-600">
                  <ImageIcon size={16} />
                  {form.logo ? "Logo seleccionado" : "Subir logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                </label>
                {previewLogo && (
                  <img src={previewLogo} alt="preview" className="w-10 h-10 rounded-xl object-cover border border-gray-200" />
                )}
              </div>
            </div>
          </div>

          {/* Datos del propietario */}
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <User size={12} /> Cuenta del propietario
            </p>
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
              <div className="relative">
                <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  placeholder="Nombre del propietario"
                  value={form.ownerName}
                  onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                />
              </div>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  placeholder="Correo electrónico *"
                  value={form.ownerEmail}
                  onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                />
              </div>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="password"
                  placeholder="Contraseña *"
                  value={form.ownerPassword}
                  onChange={(e) => setForm({ ...form, ownerPassword: e.target.value })}
                  className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400"
                />
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              Se creará una cuenta nueva con estas credenciales para el propietario del comercio.
            </p>
          </div>

          <button
            disabled={loading}
            className="w-full bg-supernova-500 hover:bg-supernova-600 text-white rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-semibold transition disabled:opacity-60"
          >
            <Plus size={16} />
            {loading ? "Creando..." : "Crear empresa"}
          </button>
        </motion.form>
      )}

      {/* Search + filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white"
            placeholder="Buscar por nombre o slug..."
            value={search}
            onChange={(e) => applySearch(e.target.value)}
          />
        </div>
        <div className="flex gap-1.5">
          {[
            { id: "all",         label: "Todas" },
            { id: "Activo",      label: "Activas" },
            { id: "Inactivo",    label: "Inactivas" },
            { id: "En_revision", label: "Revisión" },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => applyStatus(id)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition border ${filterStatus === id
                ? "bg-gray-800 text-white border-gray-800"
                : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Company grid */}
      {companies.length === 0 ? (
        <div className="text-center py-16 text-gray-300">
          <Building2 size={48} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm text-gray-400">{search ? "Sin resultados" : "No hay empresas registradas"}</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {companies.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              onClick={() => onSelectCompany?.(c)}
              className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition cursor-pointer group"
            >
              {/* Card body */}
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Logo */}
                  <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-100 shrink-0">
                    {c.logo
                      ? <img src={getImageUrl(c.logo)} alt={c.name} className="w-full h-full object-cover" />
                      : <Building2 size={20} className="text-gray-400" />
                    }
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-gray-800 truncate leading-tight">{c.name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">@{c.slug}</p>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-supernova-500 transition shrink-0 mt-0.5" />
                    </div>

                    {c.category && (
                      <span className="inline-block text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full mt-1.5">
                        {c.category.name}
                      </span>
                    )}
                  </div>
                </div>

                {/* Description */}
                {c.description && (
                  <p className="text-xs text-gray-500 mt-2.5 line-clamp-2 leading-relaxed">{c.description}</p>
                )}

                {/* Meta stats */}
                <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Package size={10} /> {c._count?.products ?? 0} productos
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin size={10} /> {c.sedes?.length ?? 0} sedes
                  </span>
                </div>
              </div>

              {/* Card footer */}
              <div className="border-t border-gray-50 px-4 py-2.5 flex items-center justify-between gap-2">
                {/* Status toggle */}
                <label
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className={`relative w-9 h-5 rounded-full transition-colors ${c.status === "Activo" ? "bg-green-500" : "bg-gray-300"}`}
                    onClick={(e) => toggleStatus(e, c.id, c.status)}
                  >
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${c.status === "Activo" ? "translate-x-4" : "translate-x-0.5"}`} />
                  </div>
                  <span className="text-xs text-gray-500">{c.status === "Activo" ? "Activa" : "Inactiva"}</span>
                </label>

                {/* Verified toggle */}
                <button
                  onClick={(e) => toggleVerification(e, c.id, c.verified)}
                  className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition ${c.verified
                    ? "bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                    : "bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100"}`}
                >
                  <BadgeCheck size={12} />
                  {c.verified ? "Verificada" : "Sin verificar"}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-gray-400">
            Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} de {total} empresas
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronLeft size={15} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "..." ? (
                  <span key={`ellipsis-${idx}`} className="px-1 text-xs text-gray-400">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-lg text-xs font-semibold transition border ${
                      p === page
                        ? "bg-gray-800 text-white border-gray-800"
                        : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
